import { Brand } from "@/shared/components/ui/Brand";
import type { SetupDetails } from "../types/paymentMethodTypes";
import { CardSetupFacts } from "./CardSetupFacts";

/** Green Froppio header with a card illustration; on large screens it becomes a left rail. */
export function CardSetupHero({ details, title, description, showCard = true, purpose = "save_card" }: {
  details?: Pick<SetupDetails, "businessName" | "customerName">; title?: string; showCard?: boolean;
  purpose?: "save_card" | "payment"; description?: string;
}) {
  const withCard = showCard && !!details;
  return (
    <aside className="lg:flex lg:min-h-full lg:flex-col lg:gap-6 lg:bg-primary lg:px-9 lg:py-10 lg:text-primary-foreground">
      <header className="flex items-center justify-between gap-4 rounded-b-[32px] bg-primary px-5 pb-6 pt-[calc(env(safe-area-inset-top)+1.25rem)] text-primary-foreground lg:block lg:rounded-none lg:p-0">
        <div className="min-w-0">
          <Brand wordmark className="text-primary-foreground" />
          <h1 className="mt-5 text-2xl font-bold leading-tight">{title ?? (purpose === "payment" ? "Pay for your appointment" : "Save your card")}</h1>
          {details && (
            <p data-telemetry-mask className="mt-0.5 text-sm text-primary-foreground/85">
              for {details.businessName} · {details.customerName}
            </p>
          )}
        </div>
        {withCard && (
          <div aria-hidden
            className="relative h-[60px] w-24 shrink-0 overflow-hidden rounded-[10px] bg-linear-to-br from-matcha to-[#a7d97a] p-2 shadow-[0_8px_16px_rgb(0_0_0/0.2)] lg:hidden">
            <span className="absolute -right-3 -top-3 size-12 rounded-full bg-white/40" />
            <Brand className="absolute right-1.5 top-1.5 [&>span:first-child]:text-[24px]" />
            <span className="block h-3 w-4 rounded-[3px] bg-linear-to-br from-[#f6e7a7] to-[#d9be5c]" />
            <p className="mt-2.5 font-mono text-[9px] tracking-widest text-foreground">•••• ••••</p>
          </div>
        )}
      </header>
      {withCard && <>
        <div aria-hidden data-telemetry-mask
          className="relative hidden h-48 overflow-hidden rounded-[22px] bg-linear-to-br from-matcha to-[#a7d97a] p-5 text-foreground shadow-[0_14px_28px_rgb(40_96_62/0.25)] lg:block">
          <span className="absolute -right-6 -top-6 size-36 rounded-full bg-white/40" />
          <Brand className="absolute right-4 top-4 [&>span:first-child]:text-[54px]" />
          <span className="block h-7 w-10 rounded-md bg-linear-to-br from-[#f6e7a7] to-[#d9be5c]" />
          <p className="mt-7 font-mono text-xl tracking-[0.14em]">•••• •••• •••• ••••</p>
          <p className="mt-4 flex justify-between gap-3 text-xs uppercase tracking-wider">
            <span className="truncate">{details.customerName}</span><span>MM/YY</span>
          </p>
        </div>
        <CardSetupFacts businessName={details.businessName} purpose={purpose} />
        {description && <p className="mx-5 mt-4 text-xs leading-relaxed text-muted-foreground lg:mx-0 lg:mt-0 lg:text-primary-foreground/85">
          {description}
        </p>}
      </>}
    </aside>
  );
}
